/**
 * api/orders.ts — HTTP contract schemas for the orders API surface.
 *
 * Covers:
 *   POST /v1/orders              → CreateOrderRequest / CreateOrderResponse
 *   GET  /v1/orders/has-ordered  → HasOrderedResponse
 *   PATCH /v1/admin/orders/:id/mark-paid → MarkPaidResponse
 *   GET  /v1/admin/orders        → AdminOrderRow
 *
 * Money fields are decimal-strings. No `any` — unknown JSONB uses
 * `z.record(z.string(), z.unknown())`.
 *
 * Source: 2026-06-25-wandero-local-orders-copy-store spec §D2.
 */

import { z } from 'zod';
import { OrderItemSnapshot, OrderCustomerSnapshot, OrderStatus, OrderType } from '../orders.js';

// ── POST /v1/orders ──────────────────────────────────────────────────────────

export const CreateOrderRequestSchema = z.object({
  /** Line-items snapshot from cart (must be non-empty, capped to prevent DoS). */
  items: z.array(OrderItemSnapshot).min(1).max(100),
  /** Customer PII snapshot. */
  customer: OrderCustomerSnapshot,
  /** Order total — decimal-string, with all stacked discounts already applied. */
  total: z.string().max(32),
  /** ISO-3 currency code (e.g. "PLN"). */
  currency: z.string().min(3).max(3),
  /** Customer email, also stored at order top-level for lookup/citext index. */
  email: z.string().email().max(254),
  /** GDPR/RODO consent for PII processing. */
  consent_pii: z.boolean(),
  /**
   * Optional explicit override — the checkout POST path is otherwise
   * UNCHANGED (2026-07-18-custom-photo-order-page D7): `doInsertOrder`
   * derives `type` server-side from `items.some(i => i.source ===
   * 'own_photo')` when this is omitted, so existing clients never need to
   * send it.
   */
  type: OrderType.optional(),
  /**
   * Bearer promo code applied at checkout (2026-06-25-wandero-email-promo-
   * code-discount, B4 checkout-promo fix). Optional — omit for orders
   * without a promo. The server ALWAYS re-validates this against
   * `promo_codes` and recomputes `total` + `items[].price` from the
   * request's own `price_total`/`price_discounted` line data via the shared
   * `promoCartTotal` helper; the client-supplied `total` is never the
   * figure that gets persisted when this field is present. `/v1/` stays
   * backward-compatible: existing clients that never send this keep working
   * unchanged.
   */
  promo_code: z.string().max(64).optional(),
});
export type CreateOrderRequest = z.infer<typeof CreateOrderRequestSchema>;

export const CreateOrderResponseSchema = z.object({
  /** Human-readable order identifier (WND-YYMMDD-XXXXXX). Not the internal UUID. */
  order_number: z.string(),
});
export type CreateOrderResponse = z.infer<typeof CreateOrderResponseSchema>;

// ── GET /v1/orders/has-ordered ───────────────────────────────────────────────

export const HasOrderedResponseSchema = z.object({
  /** True if the email has at least one order (any status). */
  has_ordered: z.boolean(),
  /** Number of orders with status in (paid, accepted, done). */
  paid_count: z.number().int().nonnegative(),
});
export type HasOrderedResponse = z.infer<typeof HasOrderedResponseSchema>;

// ── PATCH /v1/admin/orders/:id/mark-paid ────────────────────────────────────

export const MarkPaidResponseSchema = z.object({
  id: z.string().uuid(),
  order_number: z.string(),
  status: OrderStatus,
  /** Payment stub object set by mark-paid action. */
  payment: z.record(z.string(), z.unknown()).nullable(),
  updated_at: z.string(),
});
export type MarkPaidResponse = z.infer<typeof MarkPaidResponseSchema>;

// ── GET /v1/admin/orders ─────────────────────────────────────────────────────

/**
 * Admin-facing order row. Includes PII fields (email, customer) — only
 * accessible behind requireSupabaseAuth via /v1/admin/* facade.
 */
export const AdminOrderRowSchema = z.object({
  id: z.string().uuid(),
  order_number: z.string(),
  email: z.string().email(),
  status: OrderStatus,
  /** `'catalog'` (default) or `'own_photo'` — derived server-side from items. */
  type: OrderType,
  /** NUMERIC(12,2) as decimal-string. */
  total: z.string(),
  currency: z.string(),
  customer: OrderCustomerSnapshot,
  items: z.array(OrderItemSnapshot),
  payment: z.record(z.string(), z.unknown()).nullable(),
  amanager_ref: z.string().nullable(),
  consent_pii: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type AdminOrderRow = z.infer<typeof AdminOrderRowSchema>;
